import { css } from 'aphrodite';
import * as React from 'react';
import type { IAccount } from '../../../extViewmodels';
import * as Api from '../../../extViewmodels';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import type { ISupportAgentConfig } from '../../../queries/Support/useSupportTeam';
import { useAdminAccountFilterQuery } from '../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { AccountFilterCriteriaProperty, type IAccountsFilterRequest } from '../../../viewmodels/AdminViewModels';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { Button } from '../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemTitle,
} from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Pill } from '../../../web/components-v2/Pill';
import {
	getFilterOptionLabel,
	type InboxFilters,
	PRIORITY_FILTER_OPTIONS,
	STATUS_FILTER_OPTIONS,
} from './inboxFilters';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';

interface AccountScopePickerProps {
	accountId?: string;
	accountName?: string;
	onSelectAccount: (account: IAccount) => void;
	onClearAccount: () => void;
}

export const AccountScopePicker: React.FC<AccountScopePickerProps> = ({
	accountId,
	accountName,
	onSelectAccount,
	onClearAccount,
}) => {
	const [isEditing, setIsEditing] = React.useState(false);
	const [searchValue, setSearchValue] = React.useState(accountName ?? '');
	const debouncedSearchValue = useDebounceValue(searchValue.trim(), 250);
	const accountFilter = React.useMemo<IAccountsFilterRequest>(() => {
		const criteria: Api.IFilterCriteria<AccountFilterCriteriaProperty>[] = [];
		if (debouncedSearchValue) {
			criteria.push({ property: AccountFilterCriteriaProperty.Name, value: debouncedSearchValue });
		}
		return { criteria, op: Api.FilterOperator.And };
	}, [debouncedSearchValue]);
	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilter,
		autocompleteFragment: debouncedSearchValue || undefined,
		pageSize: 20,
		enabled: isEditing,
	});
	const accounts = React.useMemo(
		() => accountsQuery.data?.pages.flatMap(page => page.values ?? []) ?? [],
		[accountsQuery.data]
	);
	const selectedAccount = accounts.find(account => account.id === accountId) ?? null;

	React.useEffect(() => {
		setSearchValue(accountName ?? '');
		setIsEditing(false);
	}, [accountId, accountName]);

	if (!accountId) return null;

	if (!isEditing) {
		const label = accountName || 'Account';
		return (
			<div className={css(ts.accountScope)}>
				<Button
					type='button'
					variant='outline'
					styles={[ts.accountScopeButton]}
					title='Change account'
					onClick={() => setIsEditing(true)}
				>
					<span className={css(ts.accountScopePrefix)}>Account</span>
					<span className={css(ts.accountScopeName)} title={label}>
						{label}
					</span>
					<ChevronDown width={12} height={12} />
				</Button>
				<Button type='button' variant='ghost' styles={[ts.accountScopeClearBtn]} onClick={onClearAccount}>
					All accounts
				</Button>
			</div>
		);
	}

	return (
		<div className={css(ts.accountScope, ts.accountScopeEditing)}>
			<Combobox
				items={accounts}
				value={selectedAccount}
				onValueChange={(account: IAccount | null) => {
					if (account) onSelectAccount(account);
					else onClearAccount();
				}}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				itemToStringLabel={(account: IAccount) => account.companyName || ''}
				isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
			>
				<ComboboxInput
					aria-label='Search accounts'
					placeholder='Search accounts...'
					showClear
					styles={[ts.accountComboboxInput]}
				/>
				<ComboboxContent styles={[ts.accountComboboxContent]}>
					<ComboboxStatus>
						{accountsQuery.isPending || accountsQuery.isFetching
							? 'Searching...'
							: accounts.length === 0 && searchValue
								? `No accounts found for "${searchValue}"`
								: accounts.length > 0
									? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
									: 'Search accounts'}
					</ComboboxStatus>
					<ComboboxList>
						{(account: IAccount) => (
							<ComboboxAvatarItem key={account.id} value={account}>
								<ComboboxAvatarItemAvatar
									imageUrl=''
									fallbackText={(account.companyName || 'AC').substring(0, 2).toUpperCase()}
								/>
								<ComboboxAvatarItemTitle>{account.companyName || account.id}</ComboboxAvatarItemTitle>
							</ComboboxAvatarItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
			<Button type='button' variant='ghost' styles={[ts.accountScopeClearBtn]} onClick={onClearAccount}>
				All accounts
			</Button>
			<Button type='button' variant='ghost' styles={[ts.accountScopeClearBtn]} onClick={() => setIsEditing(false)}>
				Cancel
			</Button>
		</div>
	);
};

interface SearchCriteriaBarProps {
	searchValue: string;
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	categoryMap: Record<string, { name: string; color?: string }>;
	teamMembers: ReadonlyArray<Pick<ISupportAgentConfig, 'agentId' | 'agentName'>>;
	accountId?: string;
	accountName?: string;
	/**
	 * Drops every refinement the table already shows on its own column header, leaving CSM — the one
	 * with no column to sit on, and so the one that would otherwise narrow the list invisibly.
	 */
	csmOnly?: boolean;
}

export const SearchCriteriaBar: React.FC<SearchCriteriaBarProps> = ({
	searchValue,
	filters,
	onFiltersChange,
	categoryMap,
	teamMembers,
	accountId,
	accountName,
	csmOnly,
}) => {
	const pills: Array<{ key: string; label: string; onRemove: () => void }> = [];
	if (!csmOnly && filters.status) {
		pills.push({
			key: 'status',
			label: `Status: ${getFilterOptionLabel(STATUS_FILTER_OPTIONS, filters.status)}`,
			onRemove: () => onFiltersChange({ ...filters, status: undefined }),
		});
	}
	if (!csmOnly && filters.priority) {
		pills.push({
			key: 'priority',
			label: `Priority: ${getFilterOptionLabel(PRIORITY_FILTER_OPTIONS, filters.priority)}`,
			onRemove: () => onFiltersChange({ ...filters, priority: undefined }),
		});
	}
	if (!csmOnly && filters.categoryId) {
		pills.push({
			key: 'category',
			label: `Category: ${categoryMap[filters.categoryId]?.name ?? 'Unknown'}`,
			onRemove: () => onFiltersChange({ ...filters, categoryId: undefined }),
		});
	}
	if (!csmOnly && filters.assigneeId) {
		pills.push({
			key: 'assignee',
			label: `Assignee: ${teamMembers.find(member => member.agentId === filters.assigneeId)?.agentName ?? 'Unknown'}`,
			onRemove: () => onFiltersChange({ ...filters, assigneeId: undefined }),
		});
	}
	if (!csmOnly && filters.accountId) {
		pills.push({
			key: 'account',
			label: `Account: ${filters.accountName || filters.accountId}`,
			onRemove: () => onFiltersChange({ ...filters, accountId: undefined, accountName: undefined }),
		});
	}
	if (filters.csmName) {
		pills.push({
			key: 'csm',
			label: `CSM: ${filters.csmName}`,
			onRemove: () => onFiltersChange({ ...filters, csmId: undefined, csmName: undefined }),
		});
	}

	const query = searchValue.trim();
	// Without a query the refinements narrow the view the left-rail tabs still show, so the bar says
	// what was narrowed by rather than claiming a search is running.
	if (csmOnly && pills.length === 0) return null;

	const scope = accountId ? accountName || 'this account' : 'this mailbox';
	const searchLead = query ? `Searching ${scope} for “${query}”` : `Searching ${scope}`;
	const lead = csmOnly ? 'Filtered by:' : pills.length > 0 ? `${searchLead} with:` : searchLead;

	return (
		<div className={css(ts.searchCriteriaRow)} role='status'>
			<span className={css(ts.searchCriteriaText)}>{lead}</span>
			{pills.map(pill => (
				<Pill key={pill.key} variant='tag' color='blue' size='small' label={pill.label} onRemove={pill.onRemove} />
			))}
		</div>
	);
};
